import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import NavBar from '../../components/layout/NavBar'
import EmailSignupButton from '../../components/forms/EmailSignupButton'
import Footer from '../../components/layout/Footer'
import './HowToUse.css'

const HowToUse = () => {
  const [selectedLocation, setSelectedLocation] = useState('sf')

  const locations = {
    sf: {
      name: 'San Francisco',
      lat: 37.7749,
      lon: -122.4194
    },
    la: {
      name: 'Los Angeles', 
      lat: 34.0522,
      lon: -118.2437
    }
  }

  const getUVLevel = (uvIndex) => {
    if (uvIndex <= 2) return { level: 'Low', color: '#2ecc71', ringClass: 'uv-low' }
    if (uvIndex <= 5) return { level: 'Moderate', color: '#f1c40f', ringClass: 'uv-moderate' }
    if (uvIndex <= 7) return { level: 'High', color: '#ff8c42', ringClass: 'uv-high' }
    if (uvIndex <= 10) return { level: 'Very High', color: '#e74c3c', ringClass: 'uv-very-high' }
    return { level: 'Extreme', color: '#8e44ad', ringClass: 'uv-extreme' }
  }

  const fetchUVData = async (location) => {
    setUvData(prev => ({ ...prev, loading: true }))
    
    try {
      const { lat, lon } = locations[location]
      const apiKey = '92dac65f8b096c67bc7d2578bab91e81'
      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/uvi?lat=${lat}&lon=${lon}&appid=${apiKey}`
      )
      
      if (response.ok) {
        const data = await response.json()
        const uvLevel = getUVLevel(data.value)
        setUvData({
          value: Math.round(data.value * 10) / 10, // Round to 1 decimal
          level: uvLevel.level,
          color: uvLevel.color,
          ringClass: uvLevel.ringClass,
          loading: false
        })
      } else {
        throw new Error('Failed to fetch UV data')
      }
    } catch (error) {
      console.error('Error fetching UV data:', error)
      // Fallback to demo data
      setUvData({
        value: 8,
        level: 'High',
        color: '#ff8c42',
        ringClass: 'uv-high',
        loading: false
      })
    }
  }

  useEffect(() => {
    fetchUVData(selectedLocation)
  }, [selectedLocation])


  return (
    <div className="how-to-use-container">
      <Helmet>
        <title>How to Use - Block Automation</title>
        <meta property="og:title" content="How to Use - Block Automation" />
        <meta property="og:description" content="Learn how to use Block's sunscreen dispensers in simple steps." />
      </Helmet>
      
      <NavBar />
      
      {/* Hero Section */}
      <div className="hero-section section-container">
        <div className="max-width">
          <span className="beforeHeading">SIMPLE & EFFECTIVE</span>
          <h1 className="hero-title">How to Use a Sun Block</h1>
        </div>
      </div>
      
      {/* Compact UV Index Dashboard Section */}
      {/* <div className="dashboard-section section-container">
        <div className="max-width">
          <span className="beforeHeading centered-heading">CURRENT CONDITIONS</span>
          
          <div className="location-selector">
            <label htmlFor="location-select">Location:</label>
            <select 
              id="location-select"
              value={selectedLocation} 
              onChange={handleLocationChange}
              className="location-dropdown"
            >
              {Object.entries(locations).map(([key, location]) => (
                <option key={key} value={key}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="compact-dashboard-card">
            <div className="uv-ring-container">
              <div className={`uv-ring ${uvData.ringClass}`}>
                {uvData.loading ? (
                  <SpinningSun size="small" />
                ) : (
                  <>
                    <div className="uv-value">{uvData.value}</div>
                    <div className="uv-label">UV Index</div>
                    <div className="uv-level">{uvData.level}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div> */}
      
      {/* Step-by-Step Instructions */}
      <div className="instructions-section section-container alternate-bg">
        <div className="max-width">
          <span className="beforeHeading">EASY AS 1-2-3</span>
          <h2 className="section-title">Instructions</h2>
          
          <div className="instruction-cards">            
            <div className="instruction-card card-step-2">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Make Payment</h3>
                <p>Purchase by scanning the QR code. Each application gives you 3 pumps of premium sunscreen.</p>
                <div className="step-visual">
                  <div className="payment-options">
                    <div className="payment-icon">📱</div>
                    <div className="payment-icon">🧴🧴🧴</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="instruction-card card-step-3">
              <div className="step-content">
                <h3>Wave Your Hand</h3>
                <p>Hold your hand near the sensor and wave. The machine will dispense one pump. Repeat 3 times for a complete application.</p>
                <div className="step-visual">
                  <div className="hand-wave-demo">
                    <div className="hand-icon">👋</div>
                    <div className="wave-indicator">
                      <div className="wave-line"></div>
                      <div className="wave-line"></div>
                      <div className="wave-line"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="step-number">2</div>
            </div>
          </div>
        </div>
              </div>
        
        {/* Application Guide Section */}
        <div className="application-guide-section section-container">
          <div className="max-width">
            <span className="beforeHeading">APPLICATION GUIDES</span>
            <h2 className="section-title">Proper Application Techniques</h2>
            
            <div className="guide-cards">
              <div className="guide-card">
                <div className="guide-icon face-icon">👤</div>
                <h3>Face & Neck</h3>
                <p>Apply evenly to face and neck area. Don't forget ears, chin and back of the neck.</p>
                <div className="guide-amount">Amount: 1 pump</div>
              </div>
              
              <div className="guide-card">
                <div className="guide-icon arms-icon">💪</div>
                <h3>Arms &amp; Shoulders</h3>
                <p>Cover all exposed arm and shoulder areas thoroughly.</p>
                <div className="guide-amount">Amount: 1 pump</div>
              </div>
              
              <div className="guide-card">
                <div className="guide-icon body-icon">👕</div>
                <h3>Body & Legs</h3>
                <p>Apply to all exposed areas of torso and legs.</p>
                <div className="guide-amount">Amount: 2 pumps</div>
              </div>
              
              <div className="guide-card">
                <div className="guide-icon water-icon">🏊</div>
                <h3>Water Activities</h3>
                9<p>Reapply every 60-0 minutes when swimming or sweating heavily.</p>
                <div className="guide-amount">Frequency: Every hour</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tips & Best Practices */}
        <div className="tips-section section-container alternate-bg">
          <div className="max-width">
            <span className="beforeHeading">PRO TIPS</span>
            <h2 className="section-title">Best Practices</h2>
            
            <div className="tips-grid">
              <div className="tip-card">
                <div className="tip-icon">⏱️</div>
                <h3>Timing Matters</h3>
                <p>Wait about 2 seconds between waves to ensure proper dispensing.</p>
              </div>
              
              <div className="tip-card">
                <div className="tip-icon">🤚</div>
                <h3>Hand Positioning</h3>
                <p>Wave clearly and move your hand away - don't keep it near the sensor continuously.</p>
              </div>
              
              <div className="tip-card">
                <div className="tip-icon">🔄</div>
                <h3>Reapplication</h3>
                <p>Reapply every 2 hours or after swimming/sweating for optimal protection.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Troubleshooting Section */}
        <div className="troubleshooting-section section-container">
          <div className="max-width">
            <span className="beforeHeading">NEED HELP?</span>
            <h2 className="section-title">Common Messages</h2>
            
            <div className="troubleshooting-cards">
              <div className="trouble-card">
                <div className="trouble-message">"See you tomorrow!"</div>
                <div className="trouble-explanation">The dispenser is closed for the day. Try again during operating hours.</div>
              </div>
              
              <div className="trouble-card">
                <div className="trouble-message">&quot;Temporarily out of order&quot;</div>
                <div className="trouble-explanation">The machine needs service. Look for another nearby dispenser or contact support.</div>
              </div>
              
              <div className="trouble-card">
                <div className="trouble-message">&quot;Recalibrating sensor...&quot;</div>
                <div className="trouble-explanation">Please remove your hand and wait a moment while the machine adjusts itself.</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Call to Action Section with Orange Gradient */}
      <div className="cta-section section-container orange-gradient-bg">
        <div className="max-width">
          <div className="cta-banner">
            <span className="beforeHeading">START PROTECTING</span>
            <h2>Find a Block Dispenser Near You</h2>
            <p>
              Ready to experience the future of sun protection? Find the nearest Block dispenser 
              or bring this convenient service to your location.
            </p>
            <div className="cta-buttons">
              <button 
                className="button button-primary"
                onClick={() => window.location.href = '/partners'}
              >
                Become a Partner
              </button>
              <EmailSignupButton />
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  )
}

export default HowToUse 